import { useEffect, useMemo, useState } from 'react';
import { FileText, Mail, Printer, RefreshCw, Send, X } from 'lucide-react';
import { api } from '../lib/api';

const request=body=>({method:'POST',headers:{'content-type':'application/json'},body:JSON.stringify(body)});
const normal=value=>String(value||'').trim().replace(/\s+/g,' ').toLowerCase();

export default function CustomerEmailComposer({customerId}){
  const[open,setOpen]=useState(false);
  const[mode,setMode]=useState('email');
  const[record,setRecord]=useState(null);
  const[templates,setTemplates]=useState([]);
  const[selectedId,setSelectedId]=useState('');
  const[subject,setSubject]=useState('');
  const[messageBody,setMessageBody]=useState('');
  const[preview,setPreview]=useState(null);
  const[previewSnapshot,setPreviewSnapshot]=useState(null);
  const[generated,setGenerated]=useState(null);
  const[busy,setBusy]=useState(false);
  const[notice,setNotice]=useState('');

  useEffect(()=>{
    let active=true;
    Promise.all([
      api(`/api/customers/${encodeURIComponent(customerId)}`),
      api('/api/communications/templates?status=active'),
    ]).then(([customerData,templateData])=>{
      if(!active)return;
      setRecord(customerData);
      setTemplates(templateData.templates||[]);
    }).catch(()=>{});
    return()=>{active=false;};
  },[customerId]);

  useEffect(()=>{
    const openComposer=event=>{
      const channel=event?.detail?.channel;
      setMode(channel==='letter'?'letter':'email');
      setNotice('');setPreview(null);setPreviewSnapshot(null);setGenerated(null);setSelectedId('');setSubject('');setMessageBody('');
      setOpen(true);
    };
    window.addEventListener('customer360-open-communication-composer',openComposer);
    const intercept=event=>{
      const button=event.target?.closest?.('button');
      if(!button||button.closest('.customer-email-dialog'))return;
      const text=normal(button.textContent);
      if(text!=='email'&&text!=='email customer')return;
      event.preventDefault();event.stopPropagation();event.stopImmediatePropagation?.();
      openComposer({detail:{channel:'email'}});
    };
    document.addEventListener('click',intercept,true);
    return()=>{window.removeEventListener('customer360-open-communication-composer',openComposer);document.removeEventListener('click',intercept,true);};
  },[]);

  const recipient=useMemo(()=>{
    const customer=record?.customer||{},contacts=record?.contacts||[];
    const verified=contacts.find(item=>item.contact_type==='email'&&String(item.verification_status||'').toLowerCase()==='verified')?.contact_value;
    return customer.verified_email||verified||'';
  },[record]);

  const availableTemplates=useMemo(()=>templates.filter(item=>{
    if(item.channel!==mode)return false;
    return Number(item.direct_send_enabled)!==0;
  }),[templates,mode]);
  const selected=availableTemplates.find(item=>item.id===selectedId)||null;
  const staffEditable=selected?Number(selected.staff_editable)===1:true;

  useEffect(()=>{
    setPreview(null);setPreviewSnapshot(null);setGenerated(null);setNotice('');
    if(!selected){if(mode==='letter'){setSubject('');setMessageBody('')}return}
    setSubject('');
    if(!messageBody)setMessageBody('');
  },[selectedId,mode]);

  const renderPreview=async()=>{
    if(!selected)return;
    setBusy(true);setNotice('');
    try{
      const body=await api('/api/communications/templates/'+encodeURIComponent(selected.id)+'/render',request({
        customerId,
        messageBody,
        subjectOverride:staffEditable?subject.trim():'',
      }));
      setPreview(body.rendered||null);
      setPreviewSnapshot(body.snapshot||null);
      if(body.rendered?.subject)setSubject(body.rendered.subject);
    }catch(error){setNotice(`Error: ${error.message}`)}
    finally{setBusy(false)}
  };

  const sendEmail=async event=>{
    event.preventDefault();
    if(!recipient)return;
    setBusy(true);setNotice('');
    try{
      if(!selected)throw new Error('Choose an approved Template Library email.');
      const result=await api(`/api/customers/${encodeURIComponent(customerId)}/communications/template-send`,request({
        templateId:selected.id,
        previewId:previewSnapshot?.id||'',
      }));
      setNotice(`Email sent exactly as previewed using ${selected.name} v${result.version} and stored against Customer360.`);
      setPreview(null);setPreviewSnapshot(null);setSubject('');setMessageBody('');
    }catch(error){setNotice(`Error: ${error.message}`)}
    finally{setBusy(false)}
  };

  const generateLetter=async event=>{
    event.preventDefault();
    if(!selected)return;
    setBusy(true);setNotice('');
    try{
      const result=await api(`/api/customers/${encodeURIComponent(customerId)}/communications/letter-generate`,request({
        templateId:selected.id,
        messageBody,
      }));
      setGenerated(result);
      setPreview({subject:result.subject,text:result.text,html:result.html});
      setNotice(`Letter generated as ${result.reference} and stored against Customer360 using ${selected.name} v${result.version}.`);
    }catch(error){setNotice(`Error: ${error.message}`)}
    finally{setBusy(false)}
  };

  const printLetter=()=>{
    if(!generated?.html)return;
    const win=window.open('','_blank','noopener,noreferrer');
    if(!win)return;
    win.document.open();win.document.write('<!doctype html><html><head><title>'+escapeTitle(generated.subject||'Sousa Murray letter')+'</title></head><body>'+generated.html+'</body></html>');win.document.close();
    setTimeout(()=>{win.focus();win.print();},250);
  };

  if(!open)return null;
  return <div className="modal-backdrop crm-modal" onMouseDown={event=>event.target===event.currentTarget&&!busy&&setOpen(false)}>
    <form className="crm-dialog customer-email-dialog customer-communication-dialog" onSubmit={mode==='email'?sendEmail:generateLetter}>
      <div className="crm-dialog-head">
        <div><span className="dialog-eyebrow">Customer360 communication</span><h2 className="govuk-heading-m">{mode==='email'?'Email customer':'Generate customer letter'}</h2><p className="govuk-body">{mode==='email'?'Send an audited communication to the customer’s verified Sousa Murray email address.':'Generate a version-controlled formal letter and retain the exact rendered copy against Customer360.'}</p></div>
        <button type="button" className="icon-button" onClick={()=>setOpen(false)} disabled={busy}><X size={18}/></button>
      </div>
      <div className="crm-dialog-body">
        <div className="communication-mode-tabs">
          <button type="button" className={mode==='email'?'active':''} onClick={()=>{setMode('email');setSelectedId('');setPreview(null);setPreviewSnapshot(null);setGenerated(null)}}><Mail size={15}/>Email</button>
          <button type="button" className={mode==='letter'?'active':''} onClick={()=>{setMode('letter');setSelectedId('');setPreview(null);setPreviewSnapshot(null);setGenerated(null)}}><FileText size={15}/>Letter</button>
        </div>
        {notice&&<div className={`operation-notice ${notice.startsWith('Error:')?'error':''}`}>{notice}</div>}
        <div className="crm-form-grid">
          {mode==='email'&&<label className="crm-field wide govuk-label"><span>To</span><input className="govuk-input" value={recipient} readOnly disabled/><small>Only a verified email recorded against this Customer360 account can be used.</small></label>}
          <label className="crm-field wide govuk-label"><span>Template</span><select className="govuk-select" value={selectedId} onChange={e=>setSelectedId(e.target.value)}>
            {mode==='email'&&<option value="">Select an email template…</option>}
            {mode==='letter'&&<option value="">Select a letter template…</option>}
            {availableTemplates.map(item=><option value={item.id} key={item.id}>{item.name} · v{item.version_number}</option>)}
          </select><small>{selected?selected.category+' · '+(staffEditable?'Staff-editable content':'Controlled locked content'):'Templates are managed in Communications → Template Library.'}</small></label>
          {mode==='email'&&<label className="crm-field wide govuk-label"><span>Subject</span><input className="govuk-input" required maxLength="180" value={subject} onChange={e=>{setSubject(e.target.value);setPreview(null);setPreviewSnapshot(null)}} placeholder={selected?'Leave blank to use the template subject':'Select a Template Library email first'} disabled={selected&&!staffEditable}/></label>}
          <label className="crm-field wide govuk-label"><span>{selected?'Editable message content':'Message'}</span><textarea className="govuk-textarea" required={mode==='email'&&!selected} rows="10" maxLength="20000" value={messageBody} onChange={e=>{setMessageBody(e.target.value);setPreview(null);setPreviewSnapshot(null)}} placeholder={selected?'Content inserted into the approved template where {{message.body}} is used.':'Select a Template Library template first.'} disabled={selected&&!staffEditable}/><small>{selected&&!staffEditable?'This template is controlled. Staff cannot alter its wording before use.':'The exact rendered communication is retained when a library template is used.'}</small></label>
        </div>
        {selected&&<div className="communication-preview-actions"><button type="button" className="button secondary govuk-button govuk-button--secondary" onClick={renderPreview} disabled={busy}><RefreshCw size={14}/>Generate exact send preview</button>{generated?.html&&<button type="button" className="button secondary govuk-button govuk-button--secondary" onClick={printLetter}><Printer size={14}/>Print / save PDF</button>}</div>}
        {preview&&<div className="customer-communication-preview">
          <div><span><strong>{preview.subject||selected?.name}</strong>{mode==='email'&&previewSnapshot?.exact&&<small>Exact send snapshot · expires {new Date(previewSnapshot.expiresAt).toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit'})}</small>}</span><button type="button" onClick={()=>{setPreview(null);setPreviewSnapshot(null)}}>Close</button></div>
          {preview.html?<iframe title="Communication preview" srcDoc={preview.html}/>:<pre>{preview.text}</pre>}
          {mode==='email'&&previewSnapshot?.exact&&<p className="exact-preview-assurance govuk-body">This exact rendered HTML is locked for this send. Customer Operations will send this stored preview snapshot without rendering the template again.</p>}
        </div>}
      </div>
      <div className="crm-dialog-footer">
        <button type="button" className="button secondary govuk-button govuk-button--secondary" onClick={()=>setOpen(false)} disabled={busy}>Close</button>
        {mode==='email'?<button className="button primary govuk-button" disabled={busy||!recipient||!selected||(staffEditable&&!messageBody.trim())||!previewSnapshot?.exact}><Send size={15}/>{busy?'Sending…':selected&&!previewSnapshot?.exact?'Preview before sending':'Send exact preview'}</button>:<button className="button primary govuk-button" disabled={busy||!selected||(staffEditable&&!messageBody.trim())}><FileText size={15}/>{busy?'Generating…':'Generate letter'}</button>}
      </div>
    </form>
  </div>;
}

function escapeTitle(value){return String(value||'').replace(/[<>&"']/g,'')}
